import { Component, isDevMode, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { CdkDialogService } from "../../../../../../../../services/dialog/cdk-dialog.service";
import { UploadExcelService } from "src/app/services/upload-excel.service";
import { BroadcastService } from "src/app/services/broadcast.service";
import { IdentityRolesService } from "src/app/services/identity-roles.service";
import { MasterDataService } from "src/app/services/master-data.service";
import { MasterPolicyService } from "src/app/services/master-policy.service";
import { BaseTodoListComponent } from "../../../../../../base/base-todo-list/base-todo-list.component";
import { MfHkExportReport } from "../mf-hk-report-list/mf-hk-export-report";
import { ExcelTemplateService } from "src/app/services/excel-template.service";
import { LOCALE_ID, Inject } from "@angular/core";
import { formatDate } from "@angular/common";
import { Reports } from "src/app/models/new-product/sme/mortgage-fire/hong-kong/base/mf-hk-enums";
import { KeyValue } from "src/app/framework/domain-entity/key-value";
import { KendoDatePickerOptions } from "src/app/components/shared/models/kendo-datepicker-options";

@Component({
    selector: "app-mf-hk-report-list",
    templateUrl: "./mf-hk-report-list.component.html",
    styleUrls: ["./mf-hk-report-list.component.scss"],
    standalone: false
})
export class MfHkReportComponent
  extends BaseTodoListComponent
  implements OnInit
{
  public exportReport = new MfHkExportReport();
  public excelTemplate = new ExcelTemplateService();
  public datePickerOptionsDay = KendoDatePickerOptions.dayOptions(); // acts like cache
  public datePickerOptionsMonth = KendoDatePickerOptions.monthOptions(); // acts like cache
  public datePickerOptions = this.datePickerOptionsDay;
  public reportMaxDate = new Date();
  public requireDate: boolean = false;
  public requireDiscount: boolean = false;
  public hasDateRange: boolean = false;
  public discountList: KeyValue<any>[] = [];
  public reportList: Array<{
    text: string;
    value: string;
    requireDate: string;
  }> = new Array<{
    text: string;
    value: string;
    requireDate: string;
  }>();

  public isDDAReport: boolean = false;
  public isMFPaymentReport: boolean = false;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public override router: Router,
    public override broadcastService: BroadcastService,
    public override identityRolesService: IdentityRolesService,
    public override masterPolicyService: MasterPolicyService,
    public override masterDataService: MasterDataService,
    public override uploadService: UploadExcelService,
    public override cdkDialogService: CdkDialogService
  ) {
    super(
      router,
      broadcastService,
      identityRolesService,
      masterPolicyService,
      masterDataService,
      uploadService,
      cdkDialogService
    );

    // Page Load Initialisation
    this.reportMaxDate = this.getYesterday();
    this.exportReport.date = new Date(this.reportMaxDate);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.fetchReportList();
    this.initDiscountList();

    // Initialise UI
    this.onReportChange(this.exportReport.id.toString());
  }

  public onExport(): void {
    this.masterPolicyService.exportReport(this.exportReport).then(
      (result: any) => {
        const base64String = result.fileBytes;
        const decodedByteArray = Uint8Array.from(atob(base64String), (c) =>
          c.charCodeAt(0)
        );
        this.uploadService.generateAnchorAndTriggerDownload(
          decodedByteArray,
          result.filename
        );
      },
      (err: any) => {
        console.log(err);
      }
    );
  }

  public onReportChange(selectedReport: string): void {
    // Get Report attributes
    const report = this.reportList.find((x) => x.value == selectedReport);

    // Ensure id is always a number so switch/case strict equality works correctly
    this.exportReport.id = Number(selectedReport);

    // Initialisation
    this.isDDAReport = this.exportReport.id == Reports.DDASetupWeeklyExtraction;
    this.isMFPaymentReport = this.exportReport.id == Reports.MFPaymentReport;

    this.requireDate = report?.requireDate == "True";

    this.hasDateRange = this.requireDate && this.isMFPaymentReport;

    this.requireDiscount = this.isMFPaymentReport;

    if (this.requireDate) {
      this.initDatePickerByReport();
      this.setDatesByReport();
    }

    // Reset to default
    this.exportReport.discountIds = [];
  }

  public disabledDates = (date: Date): boolean => {
    if (this.isDDAReport) return date.getDay() !== 2;
    else return false;
  };

  public initDatePickerByReport(): void {
    switch (this.exportReport.id) {
      case Reports.MFPaymentReport:
        this.datePickerOptions = this.datePickerOptionsMonth;
        break;
      default:
        this.datePickerOptions = this.datePickerOptionsDay;
        break;
    }
  }

  public setDatesByReport(): void {
    if (this.isDDAReport) {
      const today = new Date();
      const day = today.getDay(); // 0 (Sunday) to 6 (Saturday)

      // Calculate how many days to subtract to get the last Tuesday
      const daysToLastTuesday = day >= 2 ? day - 2 : day + 5;

      // Get the last Tuesday's date
      const lastTuesday = new Date(today);
      lastTuesday.setDate(today.getDate() - daysToLastTuesday);

      // If today is Tuesday, set date to today, otherwise set to last Tuesday
      this.exportReport.date = day === 2 ? today : lastTuesday;
      this.reportMaxDate = new Date();
    } else {
      this.reportMaxDate = this.getYesterday();
      this.exportReport.date = new Date(this.reportMaxDate);
    }

    if (this.isMFPaymentReport) {
      this.exportReport.dateTo = this.reportMaxDate;
    }
  }

  public covertDate(date: Date): string {
    const answer = new Date(date);
    const year = answer.getFullYear();
    const month = answer.getMonth() + 1;
    const day = answer.getDate();
    return `${year}-${month}-${day}`;
  }

  public isDateValid(d: Date): boolean {
    if (this.requireDate) {
      const date = this.getDateOnly(formatDate(d, "dd/MM/yyyy", this.locale));
      if (!date) {
        return true;
      }

      const isValidDate = date instanceof Date && !isNaN(date.getTime());

      // Check if it is future date for reports
      const today = this.getDateOnly(
        formatDate(new Date(), "dd/MM/yyyy", this.locale)
      );

      switch (this.exportReport.id) {
        case Reports.DDASetupWeeklyExtraction:
          // Check for Tuesdays
          if (isValidDate && date.getDay() === 2 && date <= today) return true;
          break;

        default:
          // Date cannot be greater than today
          if (isValidDate && date < today) return true;
          break;
      }

      // Didn't meet any positive validation checks above (within this if-clause)
      return false;
    }
    return true;
  }

  public validDateRange(): boolean {
    if (this.requireDate && this.hasDateRange) {
      let rangeMonths: number = 0;

      if (this.isMFPaymentReport) {
        rangeMonths = 24; // 2 years
      }

      const monthsBetween = this.getMonthDifference(
        this.exportReport.date,
        this.exportReport.dateTo
      );

      return monthsBetween <= rangeMonths;
    }
    return true;
  }

  public validDateTo(): boolean {
    if (this.requireDate && this.hasDateRange) {
      // Date To cannot be earlier than Date From
      return this.exportReport.dateTo >= this.exportReport.date;
    }

    return true;
  }

  public validPresentDateTo(): boolean {
    if (this.hasDateRange) {
      return this.isDateValid(this.exportReport.dateTo);
    }

    return true;
  }

  public allowExport(): boolean {
    return (
      this.isDateValid(this.exportReport.date) &&
      this.validPresentDateTo() &&
      this.validDateTo() &&
      this.validDateRange()
    );
  }

  public getDateOnly(date: string): Date {
    const parts = date.split("/");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  public initDiscountList() {
    this.masterDataService
      .getDiscountList()
      .subscribe((response: KeyValue<any>[]) => {
        this.discountList = response;
      }),
      (err: any) => {
        console.log(err);
      };
  }

  public fetchReportList() {
    if (this.identityRolesService.isUnderwriter) {
      this.masterDataService
        .getUnderwriterReportList()
        .subscribe((response) => {
          response.forEach((mp) => {
            this.reportList.push({
              text: mp.value,
              value: mp.key,
              requireDate: mp.require == undefined ? "False" : mp.require,
            });
          });

          if (this.reportList != undefined && this.reportList.length > 1) {
            // Initialise ExportReport Id
            this.exportReport.id = Number(this.reportList[0].value);
          }
        }),
        (err: any) => {
          console.log(err);
        };
    }
  }

  private getYesterday(): Date {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  }

  private getMonthDifference(dateFrom: Date, dateTo: Date): number {
    let months;
    // Calculate the difference in years and convert to months
    months = (dateTo.getFullYear() - dateFrom.getFullYear()) * 12;
    // Subtract the month of the start date
    months -= dateFrom.getMonth();
    // Add the month of the end date
    months += dateTo.getMonth();
    // Return the difference, ensuring it's not negative
    return months <= 0 ? 0 : months;
  }
}
